// lint-internal-leaks-ignore-next-line: framework-script — load-bearing visual regression marker
// @visual-diff-render: vite
/**
 * @file AssessmentProgress — compact progress indicator for assessments
 * @version 0.2.0
 */

import React, { forwardRef, type ElementType, type HTMLAttributes } from 'react';
import { tokens } from '../../../theme/tokens';
import styles from './AssessmentProgress.module.css';

/*
 * AssessmentProgress — Compact progress indicator pairing a uppercase
 * percent label (left) with a thin horizontal track-and-fill bar (right).
 * Designed for dark surfaces — the track is a 20%-alpha white wash, the
 * fill is pure white, and the label reads in light grey small-caps. A
 * single `percent` prop drives both the fill width and the label text.
 *
 *   <AssessmentProgress percent={32} />
 *   <AssessmentProgress percent={80} label="80 of 100" animated={false} />
 *
 * The fill width animates over 400ms when `percent` changes; the animation
 * is automatically disabled under `prefers-reduced-motion: reduce`.
 */

const TRACK_WIDTH = 180;
const TRACK_HEIGHT = 2;
const GAP = 14;

const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, value));

export interface AssessmentProgressProps extends HTMLAttributes<HTMLDivElement> {
  /** Completion percentage 0–100. Drives the bar fill width and the label
   *  text. Values are clamped to `[0, 100]`. */
  percent: number;
  /** Optional override for the visible label. Defaults to
   *  `${Math.round(percent)}%`. Also drives the `aria-valuetext` announced
   *  to screen readers (defaults to `${Math.round(percent)} percent
   *  complete`), so sighted and AT users hear a consistent value. The bar
   *  fill width is always derived from `percent` regardless of the label
   *  string.
   *
   *  Note: `label` drives the announced *value* (`aria-valuetext`), not the
   *  widget's accessible *name*. Pass `aria-label` separately for a
   *  context-specific name (e.g. `"Business Impact Inventory progress"`);
   *  it defaults to `"Progress"` when omitted so the progressbar always has
   *  a name. */
  label?: string;
  /** Default `true`. When `true`, the fill width transitions over 400ms on
   *  `percent` change. Automatically disabled under
   *  `prefers-reduced-motion: reduce` regardless of this prop. */
  animated?: boolean;
  /**
   * Colour treatment. `dark` (default) is the white fill + white-@20% track +
   * grey label for dark surfaces; `light` is navy fill + navy-@20% track +
   * dark-blue label for the light-surface flow. A per-instance variant.
   */
  theme?: 'light' | 'dark';
  /** Render the root as a different element/component. Default `'div'`. */
  as?: ElementType;
}

export const AssessmentProgress = forwardRef<HTMLDivElement, AssessmentProgressProps>(
  function AssessmentProgress(
    {
      percent,
      label,
      animated = true,
      className,
      style,
      theme = 'dark',
      as,
      ...rest
    },
    ref,
  ) {
  const Component = as ?? 'div';
  const rootClass = [theme === 'light' ? styles.light : '', className]
    .filter(Boolean)
    .join(' ') || undefined;
  const safePercent = clamp(percent, 0, 100);
  const fillWidth = (safePercent / 100) * TRACK_WIDTH;
  const visibleLabel = label ?? `${Math.round(safePercent)}%`;
  // aria-valuenow/min/max below already make AT auto-compute + announce a
  // percentage, so this humanized phrase goes on aria-valuetext (which
  // overrides that auto-computed value) rather than aria-label — putting it
  // on aria-label too would double-announce the same number as if it were
  // the widget's name. aria-label instead carries the widget's NAME — pulled
  // out of `rest` so a default can be supplied when the consumer doesn't pass
  // one (a progressbar with no name at all fails WCAG 4.1.2).
  const valueText = label ?? `${Math.round(safePercent)} percent complete`;
  const { 'aria-label': ariaLabelProp, ...restWithoutAriaLabel } = rest;
  const ariaLabel = ariaLabelProp ?? 'Progress';

  return (
    <Component
      ref={ref}
      role="progressbar"
      aria-valuenow={Math.round(safePercent)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuetext={valueText}
      aria-label={ariaLabel}
      className={rootClass}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: GAP,
        ...style,
      }}
      {...restWithoutAriaLabel}
    >
      <span
        aria-hidden="true"
        style={{
          fontFamily: tokens.font['family-avenir'],
          fontSize: 16,
          fontWeight: 500,
          lineHeight: '24px',
          letterSpacing: 'normal',
          textAlign: 'right',
          color: 'var(--hd-c-assessmentprogress-label-fg)',
          whiteSpace: 'nowrap',
        }}
      >
        {visibleLabel}
      </span>
      <div
        aria-hidden="true"
        style={{
          position: 'relative',
          width: TRACK_WIDTH,
          height: TRACK_HEIGHT,
          borderRadius: 999,
          backgroundColor: 'var(--hd-c-assessmentprogress-track-bg)',
          overflow: 'hidden',
        }}
      >
        <div
          className={animated ? styles.fillAnimated : undefined}
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: fillWidth,
            height: TRACK_HEIGHT,
            borderRadius: 999,
            backgroundColor: 'var(--hd-c-assessmentprogress-fill-bg)',
          }}
        />
      </div>
    </Component>
  );
  },
);

AssessmentProgress.displayName = 'AssessmentProgress';
